/**
 * @file 読書の候補一覧を取得する hook。
 *
 * @what: familyId ごとに 1 回読む。空配列 = まだ何も取り込んでいない。
 * @why: 候補一覧は家族の持ち物で学習者に従属しないため、plan の draft 機構とは独立に持つ
 *   (本だなと同じ形)。
 * @failure: 失敗は `error` (画面は 1 行で知らせる。詳細は console のみ)。backend が未デプロイの
 *   間は 404 になるので、ここは「読み込めなかった」に倒して画面を壊さない。
 */

import { useEffect, useState } from "react";
import { fetchReadingCatalogs, type StudyReadingCatalog } from "../api/readingCatalogs";

export type ReadingCatalogsState =
  | { status: "loading" }
  | { status: "ready"; catalogs: StudyReadingCatalog[] }
  | { status: "error" };

export function useReadingCatalogs(familyId: string, enabled: boolean): ReadingCatalogsState {
  const [state, setState] = useState<ReadingCatalogsState>({ status: "loading" });

  useEffect(() => {
    if (!enabled) return;
    const controller = new AbortController();
    setState({ status: "loading" });
    fetchReadingCatalogs(familyId, controller.signal)
      .then((catalogs) => {
        if (!controller.signal.aborted) setState({ status: "ready", catalogs });
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        console.error("[study] failed to load reading catalogs", err);
        setState({ status: "error" });
      });
    return () => controller.abort();
  }, [familyId, enabled]);

  return state;
}
